import { useState, useCallback } from 'react';
import { MessageType } from '../../types/message';
import { ChatType } from '../../api/chats';
import { PlayableResult } from '../../components/playables-catalog';

interface UseRegenerateProps {
  chatObj: ChatType | null;
  setChatObj: (chat: ChatType) => void;
  updateMessages: (messages: MessageType[]) => void;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  isMobile: boolean;
  activePlayable: PlayableResult | null;
}

export const useRegenerate = ({
  chatObj,
  setChatObj,
  updateMessages,
  messagesContainerRef,
  isMobile,
  activePlayable
}: UseRegenerateProps) => {
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);

  const handleRegenerate = useCallback(async (messageId: string) => {
    if (!chatObj) return;
    
    try {
      // Set regenerating state first to trigger animation
      setRegeneratingMessageId(messageId);
      
      // Only scroll if not on mobile - this prevents the scroll issues on Android
      if (!isMobile) {
        // Find the DOM element for the message being regenerated
        const messagesContainer = messagesContainerRef.current;
        if (messagesContainer) {
          // Find the message element by its ID
          const messageElement = messagesContainer.querySelector(`[data-message-id="${messageId}"]`);
          
          if (messageElement) {
            // Check if the top of the message is already in view
            const containerRect = messagesContainer.getBoundingClientRect();
            const messageRect = messageElement.getBoundingClientRect();
            
            // If the top of the message is not visible (above the viewport)
            if (messageRect.top < containerRect.top) {
              // Scroll to the top of the message
              messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        }
      }
      
      // Call the API to regenerate the message
      const updatedChat = await chatObj.regenerate(messageId, activePlayable?.id as any);
      
      // Update the chat object
      setChatObj(updatedChat);
      
      // Update messages with the new content
      updateMessages(updatedChat.messages || []);
      
    } catch (err) {
      console.error('Failed to regenerate message:', err);
    } finally {
      // Clear regenerating state
      setRegeneratingMessageId(null);
    }
  }, [
    chatObj,
    isMobile,
    messagesContainerRef,
    setChatObj,
    updateMessages,
    activePlayable
  ]);

  return { handleRegenerate, regeneratingMessageId };
};

export default useRegenerate; 